<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { fetchHealth, type HealthResponse } from "../api/chat";
import { brandRef } from "../brand-state";
import LoginButton from "./LoginButton.vue";

const health = ref<HealthResponse | null>(null);

// Debug pills (version / chat model / resolver / auth mode) are useful
// while operators are wiring environments but noise for end users.
// Surface them only when `?debug=1` is in the URL — kept on the URL so
// engineers can share a link that keeps them visible while regular
// users get a clean header.
const showDebug = computed(() => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("debug") === "1";
});

// ── Mobile nav: below ~640px the action row collapses behind a hamburger
// and drops down as a menu. Desktop ignores `menuOpen` entirely (CSS keeps
// the row inline). Closes on: navigation (hashchange), a click inside the
// menu (nav buttons), an outside click, or Escape.
const menuOpen = ref(false);
const headerEl = ref<HTMLElement | null>(null);
function closeMenu() {
  menuOpen.value = false;
}
function onDocClick(e: MouseEvent) {
  if (
    menuOpen.value &&
    headerEl.value &&
    !headerEl.value.contains(e.target as Node)
  ) {
    menuOpen.value = false;
  }
}
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") menuOpen.value = false;
}

onMounted(async () => {
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKey);
  window.addEventListener("hashchange", closeMenu);
  health.value = await fetchHealth();
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
  document.removeEventListener("keydown", onKey);
  window.removeEventListener("hashchange", closeMenu);
});
</script>

<template>
  <header ref="headerEl" class="brand-header glass-strong">
    <a class="brand-header__title" href="#/hub" aria-label="Go to the home hub">
      <img
        v-if="brandRef.logoUrl"
        :src="brandRef.logoUrl"
        :alt="brandRef.productName"
        class="brand-header__logo"
      />
      <!-- When the tenant ships no logo, the aurora-spark mark stands in so
           the wordmark still reads as a brand, not bare text. `markGlyphUrl`
           puts the tenant's monogram *on* that gradient (DOIT's D), rather
           than replacing the mark the way `logoUrl` does. -->
      <span
        v-else
        class="brand-header__mark"
        :class="{ 'brand-header__mark--glyph': brandRef.markGlyphUrl }"
        :style="
          brandRef.markGlyphUrl
            ? {
                '--mark-glyph': `url(${JSON.stringify(brandRef.markGlyphUrl)})`,
              }
            : undefined
        "
        aria-hidden="true"
      ></span>
      <span class="brand-header__words">
        <span class="brand-header__name display">{{
          brandRef.productName
        }}</span>
        <span class="brand-header__tagline">{{ brandRef.tagline }}</span>
      </span>
    </a>
    <!-- Mobile-only trigger; CSS hides it on desktop where the row is inline. -->
    <button
      type="button"
      class="brand-header__burger"
      :aria-expanded="menuOpen"
      aria-label="Menu"
      @click.stop="menuOpen = !menuOpen"
    >
      <span aria-hidden="true">{{ menuOpen ? "✕" : "☰" }}</span>
    </button>

    <div
      class="brand-header__meta"
      :class="{ 'brand-header__meta--open': menuOpen }"
      @click="closeMenu"
    >
      <template v-if="showDebug && health">
        <span class="mono">v{{ health.version }}</span>
        <span class="mono">chat: {{ health.chat_model }}</span>
        <span class="mono">rbac: {{ health.rbac_resolver }}</span>
        <span v-if="health.jwt_enabled" class="mono">auth: jwt</span>
      </template>
      <slot name="actions" />
      <LoginButton />
    </div>
  </header>
</template>

<style scoped>
.brand-header {
  position: sticky;
  top: var(--space-3);
  z-index: 50;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-5);
}
.brand-header__title {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  text-decoration: none;
  color: inherit;
}
/* Aurora-spark mark — a squircle carrying the same light-field as the
   background, with a soft cast glow. The brand's identity in 34px. */
.brand-header__mark {
  position: relative;
  flex: none;
  width: 2.15rem;
  height: 2.15rem;
  border-radius: 32%;
  background:
    radial-gradient(120% 120% at 25% 20%, var(--aurora-4), transparent 55%),
    radial-gradient(120% 120% at 80% 25%, var(--aurora-2), transparent 55%),
    radial-gradient(130% 130% at 60% 95%, var(--aurora-3), transparent 60%),
    var(--aurora-1);
  box-shadow:
    inset 0 1px 1px rgba(255, 255, 255, 0.45),
    var(--shadow-sm);
}
/* The tenant glyph, centred on the gradient. Sized in % of the mark so it
   tracks any future change to the mark's dimensions, and given a soft cast
   shadow so a white monogram keeps its edge over the lighter aurora
   stops. */
.brand-header__mark--glyph::after {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--mark-glyph) center / 46% auto no-repeat;
  filter: drop-shadow(0 1px 1.5px rgb(0 0 0 / 0.28));
}
.brand-header__logo {
  display: block;
  max-height: 2rem;
  object-fit: contain;
}
.brand-header__words {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.brand-header__name {
  font-size: 1.5rem;
  font-weight: 560;
  font-variation-settings:
    "SOFT" 20,
    "WONK" 0,
    "opsz" 40;
  letter-spacing: -0.01em;
  color: var(--color-ink);
}
.brand-header__tagline {
  margin: 0;
  color: var(--color-muted);
  font-size: 0.82rem;
}
.brand-header__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-muted);
  font-size: 0.8rem;
}

/* Hamburger — only ever shown on mobile (see media query). */
.brand-header__burger {
  display: none;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--glass-border);
  background: var(--glass-bg);
  color: var(--color-ink);
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
}

@media (max-width: 640px) {
  .brand-header__tagline {
    display: none;
  }
  .brand-header__burger {
    display: inline-flex;
  }
  /* The action row drops out of flow and becomes a dropdown panel anchored
     to the header's bottom-right; the header row is just brand + burger. */
  .brand-header__meta {
    position: absolute;
    top: calc(100% + 0.4rem);
    right: 0;
    z-index: 60;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-2);
    min-width: 12rem;
    max-width: min(20rem, 82vw);
    padding: var(--space-3);
    border-radius: var(--radius-lg);
    background: var(--color-surface);
    border: 1px solid var(--glass-border);
    box-shadow: var(--shadow-lg);
    display: none;
  }
  .brand-header__meta--open {
    display: flex;
  }
  /* Nav items go full-width in the dropdown so they're easy tap targets. */
  .brand-header__meta :slotted(*) {
    width: 100%;
  }
}
</style>
